import {
    resendVerificationApiV1AuthResendVerificationPost,
    verifyEmailApiV1AuthVerifyEmailPost,
} from '@/common/api/client/sdk.gen'
import type {
    ResendVerificationApiV1AuthResendVerificationPostData,
    ResendVerificationApiV1AuthResendVerificationPostError,
    ResendVerificationApiV1AuthResendVerificationPostResponse,
    VerifyEmailApiV1AuthVerifyEmailPostData,
    VerifyEmailApiV1AuthVerifyEmailPostResponse,
} from '@/common/api/client/types.gen'
import { useFormMutation } from '@/common/hooks/useFormMutation'
import type { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ROUTES } from '../lib/constants'

export const useVerifyEmail = () => {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')
    const navigate = useNavigate()
    const [hasAttempted, setHasAttempted] = useState(false)

    const mutation = useMutation<
        VerifyEmailApiV1AuthVerifyEmailPostResponse,
        Error,
        VerifyEmailApiV1AuthVerifyEmailPostData['body']
    >({
        mutationFn: data => verifyEmailApiV1AuthVerifyEmailPost({ body: data }).then(r => r.data!),
        retry: false,
        onSuccess: () => navigate(ROUTES.APP),
        onError: (error: Error) => {
            const axiosError = error as AxiosError<{ detail?: string }>
            const detail = axiosError?.response?.data?.detail ?? error.message ?? 'Неизвестная ошибка'
            throw new Error(detail)
        },
    })

    useEffect(() => {
        if (token && !hasAttempted) {
            setHasAttempted(true)
            mutation.mutate({ token })
        } else if (!token) {
            navigate(ROUTES.APP)
        }
    }, [token, navigate, hasAttempted, mutation])

    return { mutation }
}

export const useResendVerification = () => {
    const navigate = useNavigate()
    const { mutation, onSubmit, register, handleSubmit, errors, setError } = useFormMutation<
        ResendVerificationApiV1AuthResendVerificationPostData['body'],
        ResendVerificationApiV1AuthResendVerificationPostResponse,
        ResendVerificationApiV1AuthResendVerificationPostError
    >({
        mutationFn: data => resendVerificationApiV1AuthResendVerificationPost({ body: data }).then(r => r.data!),
        toastOptions: {
            successMessage: 'Письмо с подтверждением отправлено',
            errorMessage: 'Не удалось отправить письмо',
        },
        onSuccess: () => navigate(ROUTES.APP),
        onError: ({ response }) => {
            setError('root', { type: 'server', message: (response?.data?.detail as string) ?? 'Ошибка' })
        },
    })

    return { mutation, register, handleSubmit, errors, onSubmit }
}
